import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import settings, { paginationType } from "../../../apiConfigs/settings";
import { ReactComponent as SearchSVG } from "../../../assets/icons/search.svg";
import Pagination from "../../../components/Pagination";
import Button from "../../../designComponents/Button";
import DeleteModal from "../../../designComponents/DeleteModal";
import Input from "../../../designComponents/Input";
import Table from "../../../designComponents/Table";
import { debounce } from "../../../utils/debounce";
import requestAPI from "../../../utils/requestAPI";
import { SearchInputWrapperStyled } from "../../Users/style";
import AddEmployer from "./AddEmployer";

type Props = {};

type EmployerType = {
  _id: string;
  avatar: string;
  name: string;
  country: string;
  address: string;
  createdAt: string;
};

const Employer = (props: Props) => {
  const navigate = useNavigate();
  const [employerData, setEmployerData] = useState<EmployerType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

  const [openCreateEmployerModal, setOpenCreateEmployerModal] =
    useState<boolean>(false);
  const [paginationParams, setPaginationParams] = useState({
    page: 1,
    limit: 10,
    search: "",
    totalPages: 1,
    total: 0,
  });
  const [selectedEmployer, setSelectedEmployer] = useState<EmployerType | null>(
    null
  );
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);

  const getAllEmployer = async (params: paginationType) => {
    setLoading(true);
    try {
      const query = {
        page: params.page,
        limit: params.limit,
        search: params.search,
      };
      const response = await requestAPI(settings.getEmployer(query));
      setEmployerData(response.data);
      setPaginationParams((prevParams) => ({
        ...prevParams,
        totalPages: response.pagination.totalPages,
        total: response.pagination.total,
      }));
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const delayedFetch = useCallback(debounce(getAllEmployer, 300), []);

  useEffect(() => {
    delayedFetch(paginationParams);
  }, [delayedFetch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPaginationParams((prevParams) => {
      const updatedParams = {
        ...prevParams,
        search: value,
        page: 1,
      };
      delayedFetch(updatedParams);
      return updatedParams;
    });
  };

  const onCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
    setSelectedEmployer(null);
  };

  const handleCurrentPage = (page: number) => {
    delayedFetch({ ...paginationParams, page });
    setPaginationParams((prevParams) => ({ ...prevParams, page }));
  };

  const handleLimitChange = (limit: number) => {
    delayedFetch({ ...paginationParams, limit, page: 1 });
    setPaginationParams((prevParams) => ({ ...prevParams, limit, page: 1 }));
  };

  const handleEdit = (employer: EmployerType) => {
    setSelectedEmployer(employer);
    setOpenCreateEmployerModal(true);
  };

  const handleDelete = async (id: string) => {
    setDeleteLoading(true);
    try {
      await requestAPI(settings.deleteEmployer(id));
      getAllEmployer(paginationParams);
      setDeleteLoading(false);
      onCloseDeleteModal();
    } catch (error) {
      console.log(error);
      setDeleteLoading(false);
    }
  };

  const tableData = {
    headings: [
      { key: "avatar", label: "Avatar" },
      { key: "name", label: "Employer Name" },
      { key: "country", label: "Country" },
      { key: "address", label: "Address" },
      { key: "date", label: "Created Date" },
      { key: "actions", label: "Actions" },
    ],
    body: Array.isArray(employerData)
      ? employerData?.map((employer) => ({
          id: employer._id,
          avatar: (
            <img
              src={employer.avatar}
              alt={employer.name}
              style={{ width: "50px", height: "50px" }}
            />
          ),
          name: employer.name,
          country: employer.country,
          address: employer.address,
          date: employer.createdAt?.split("T")[0],
          actions: (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "5px",
              }}
            >
              <Button radius="normal" onClick={() => handleEdit(employer)}>
                Edit
              </Button>
              <Button
                radius="normal"
                onClick={() => {
                  setIsOpenDeleteModal(true);
                  setSelectedEmployer(employer);
                }}
                variant="danger"
              >
                Delete
              </Button>
            </div>
          ),
        }))
      : [],
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "25px",
        }}
      >
        <div>
          <SearchInputWrapperStyled>
            <Input
              value={paginationParams.search}
              name="employer"
              onChange={handleInputChange}
              placeholder="Search Employer"
              iconLeft={<SearchSVG />}
            />
          </SearchInputWrapperStyled>
        </div>
        <Button
          radius="normal"
          onClick={() => setOpenCreateEmployerModal(true)}
        >
          Add Employer
        </Button>
      </div>

      {openCreateEmployerModal && (
        <AddEmployer
          isOpenModal={openCreateEmployerModal}
          onClose={() => setOpenCreateEmployerModal(false)}
          initialValues={
            selectedEmployer || {
              avatar: "",
              name: "",
              country: "",
              address: "",
            }
          }
          refreshData={getAllEmployer}
        />
      )}

      <Table
        tableData={tableData}
        isLoading={loading}
        noData="No data to display."
        headingBgColor="#f0f0f0"
        showMenuIcon={false}
        evenRowBgColor="#fafafa"
      />
      <Pagination
        activePage={paginationParams.page}
        totalPages={paginationParams.totalPages}
        limit={paginationParams.limit}
        onCurrentPageChange={handleCurrentPage}
        onLimitChange={handleLimitChange}
      />
      {isOpenDeleteModal && (
        <DeleteModal
          isOpen={true}
          itemName={selectedEmployer?.name || ""}
          onDelete={() =>
            selectedEmployer?._id && handleDelete(selectedEmployer?._id)
          }
          onClose={onCloseDeleteModal}
          title="Are you sure want to delete this employer?"
          loading={deleteLoading}
        />
      )}
    </div>
  );
};

export default Employer;
